<template>
  <header class="fixed z-50 w-full bg-white shadow-md transition-all duration-300">
    <div class="container flex justify-between items-center px-4 mx-auto h-16 md:px-6 md:h-20">
      <!-- Logo/Brand -->
      <router-link to="/" class="flex items-center transition-transform duration-300 hover:scale-105">
        <img src="https://www.ticketjobhub.com/logo.png" alt="Logo Icon" class="hidden mr-2 w-8 h-8 sm:block">
        <!-- 电脑版显示完整名称，手机版显示简称 -->
        <span class="hidden text-2xl md:text-3xl md:block font-['Pacifico'] text-red-600">Ticket Job Hub</span>
        <span class="text-2xl md:hidden font-['Pacifico'] text-red-600">Ticket Job Hub</span>
      </router-link>
      
      <!-- Mobile Menu Button -->
      <button 
        @click="isOpen = !isOpen"
        class="text-gray-700 transition-colors duration-300 lg:hidden hover:text-red-600 focus:outline-none"
      >
        <svg
          v-if="!isOpen"
          class="block w-6 h-6"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
        <svg
          v-if="isOpen"
          class="block w-6 h-6"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
      
      <!-- Desktop Navigation -->
      <nav class="hidden items-center space-x-8 lg:flex">
        <router-link to="/" class="relative font-medium text-gray-900 transition-colors duration-300 hover:text-red-600 group">
          {{ $t('navbar.home') }}
          <span class="absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 transition-all duration-300 group-hover:w-full"></span>
        </router-link>
        <router-link to="/jobs" class="relative font-medium text-gray-900 transition-colors duration-300 hover:text-red-600 group">
          {{ $t('navbar.jobs') }}
          <span class="absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 transition-all duration-300 group-hover:w-full"></span>
        </router-link>
        <router-link to="/companies" class="relative font-medium text-gray-900 transition-colors duration-300 hover:text-red-600 group">
          {{ $t('navbar.companies') }}
          <span class="absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 transition-all duration-300 group-hover:w-full"></span>
        </router-link>
        <router-link to="/about" class="relative font-medium text-gray-900 transition-colors duration-300 hover:text-red-600 group">
          {{ $t('navbar.about') }}
          <span class="absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 transition-all duration-300 group-hover:w-full"></span>
        </router-link>
        <router-link to="/customer-service" class="relative font-medium text-gray-900 transition-colors duration-300 hover:text-red-600 group">
          {{ $t('navbar.customerService') }}
          <span class="absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 transition-all duration-300 group-hover:w-full"></span>
        </router-link>
      </nav>
      
      <!-- Desktop Buttons -->
      <div class="hidden items-center space-x-4 lg:flex">
        <LanguageSelector />
        <button @click="jumpTo()"
          class="px-6 py-2 font-medium text-red-600 whitespace-nowrap rounded-md border border-red-600 transition-all duration-300 transform hover:bg-red-600 hover:text-white hover:shadow-md hover:-translate-y-0.5">{{ $t('navbar.signIn') }}</button>
        <button @click="jumpTo('register')"
          class="px-6 py-2 font-medium text-white whitespace-nowrap bg-red-600 rounded-md transition-all duration-300 transform hover:bg-red-700 hover:shadow-md hover:-translate-y-0.5">{{ $t('navbar.register') }}</button>
      </div>
    </div>
    
    <!-- Mobile Menu -->
    <div
      v-show="isOpen"
      class="absolute w-full bg-white shadow-lg lg:hidden animate-fadeIn"
    >
      <div class="px-4 py-3 space-y-3">
        <router-link
          to="/"
          class="block py-3 font-medium text-gray-900 rounded-md transition-colors duration-300 hover:text-red-600 hover:bg-gray-50"
          @click.native="isOpen = false"
        >
          {{ $t('navbar.home') }}
        </router-link>
        <router-link
          to="/jobs"
          class="block py-3 font-medium text-gray-900 rounded-md transition-colors duration-300 hover:text-red-600 hover:bg-gray-50"
          @click.native="isOpen = false"
        >
          {{ $t('navbar.jobs') }}
        </router-link>
        <router-link
          to="/companies"
          class="block py-3 font-medium text-gray-900 rounded-md transition-colors duration-300 hover:text-red-600 hover:bg-gray-50"
          @click.native="isOpen = false"
        >
          {{ $t('navbar.companies') }}
        </router-link>
        <router-link
          to="/about"
          class="block py-3 font-medium text-gray-900 rounded-md transition-colors duration-300 hover:text-red-600 hover:bg-gray-50"
          @click.native="isOpen = false"
        >
          {{ $t('navbar.about') }}
        </router-link>
        <router-link
          to="/customer-service"
          class="block py-3 font-medium text-gray-900 rounded-md transition-colors duration-300 hover:text-red-600 hover:bg-gray-50"
          @click.native="isOpen = false"
        >
          {{ $t('navbar.customerService') }}
        </router-link>
        
        <!-- Mobile Language Selector -->
        <div class="pt-4 border-t border-gray-200">
          <LanguageSelector />
        </div>
        <!-- Mobile Buttons -->
        <div class="flex flex-col pt-4 space-y-3 border-t border-gray-200">
          <button @click="jumpTo()"
            class="py-3 w-full font-medium text-center text-red-600 rounded-md border border-red-600 transition-all duration-300 hover:bg-red-600 hover:text-white">{{ $t('navbar.signIn') }}</button>
          <button @click="jumpTo('register')"
            class="py-3 w-full font-medium text-center text-white bg-red-600 rounded-md transition-all duration-300 hover:bg-red-700">{{ $t('navbar.register') }}</button>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import { jumpTo } from '@/utils/navigation';
import LanguageSelector from './LanguageSelector.vue';
export default {
  name: 'TheNavbar',
  components: {
    LanguageSelector
  },
  data() {
    return {
      isOpen: false
    }
  },
  methods: {
    jumpTo
  }
}
</script>